import { type LinksFunction, type MetaFunction } from '@remix-run/node'
import { type ShouldRevalidateFunctionArgs } from '@remix-run/react'
import keenSliderStylesheetHref from 'keen-slider/keen-slider.min.css?url'
import isEqual from 'lodash.isequal'
import uniqWith from 'lodash.uniqwith'
import 'moment/locale/de.js'
import 'moment/locale/el.js'
import 'moment/locale/es.js'
import 'moment/locale/fr.js'
import 'moment/locale/it.js'
import 'moment/locale/nl.js'
import 'moment/locale/pt.js'
import 'moment/locale/ru.js'
import 'moment/locale/tr.js'
import 'moment/locale/zh-cn.js'

import { appConfig } from '#app/app-config.ts'
import { ADD_TO_WISHLIST_FORM } from '#app/components/forms/add-to-wishlist-form/add-to-wishlist-form.tsx'
import { CREATE_ADDRESS_FORM } from '#app/components/forms/create-address-form/create-address-form.tsx'
import { CREATE_PRODUCT_REVIEW_FORM } from '#app/components/forms/create-product-review-form/create-product-review-form.tsx'
import { EDIT_ADDRESS_FORM } from '#app/components/forms/edit-address-form/edit-address-form.tsx'
import { NEWSLETTER_FORM } from '#app/components/forms/newsletter-form/newsletter-form.tsx'
import { REMOVE_ADDRESS_FORM } from '#app/components/forms/remove-address-form/remove-address-form.tsx'
import { REMOVE_FROM_WISHLIST_FORM } from '#app/components/forms/remove-from-wishlist-form/remove-from-wishlist-form.tsx'
import { RESEND_VERIFICATION_EMAIL_FORM } from '#app/components/forms/resend-verification-email-form/resend-verification-email-form.tsx'
import { SET_DEFAULT_ADDRESS_FORM } from '#app/components/forms/set-default-address-form/set-default-address-form.tsx'
import i18nConfig from '#app/i18n'
import { PRODUCTS_INDEX_SELECT_TAXON_SEARCH_PARAMS } from '#app/routes/($lang).products._index/taxon-filter'
import { localesMap } from '#app/routes/_layout/i18n'
import {
	ROOT_I18NEXT,
	ROOT_INPUTS,
	ROOT_SEARCH_PARAMS,
} from '#app/routes/_layout/route-config.ts'
import globalStylesheetHref from '#app/styles/global.css?url'
import { defaultMetaI18next } from '#app/utils/client/default-meta-i18next.ts'
import { type loader } from './server.route.ts'

/* -------------------------------------------------------------------------------------------------
 * Handle
 * -----------------------------------------------------------------------------------------------*/

const handle = { i18n: [...Object.values(ROOT_I18NEXT)] }

/* -------------------------------------------------------------------------------------------------
 * Meta
 * -----------------------------------------------------------------------------------------------*/

const meta: MetaFunction<typeof loader> = ({ data, location }) => {
	return uniqWith(
		data
			? [
					{ title: data.meta.title },
					{
						name: 'description',
						content: data.meta.description,
					},
					// Crawler
					{
						name: 'robots',
						content: 'index,follow',
					},
					{
						name: 'googlebot',
						content: 'index,follow',
					},
					// Open Graph
					{
						property: 'og:title',
						content: appConfig.seo.openGraph.title,
					},
					{
						property: 'og:description',
						content: appConfig.seo.openGraph.description,
					},
					{
						property: 'og:type',
						content: appConfig.seo.openGraph.type,
					},
					{
						property: 'og:site_name',
						content: appConfig.seo.openGraph.site_name,
					},
					{
						property: 'og:url',
						content: appConfig.seo.openGraph.url,
					},
					// Twitter
					{
						name: 'twitter:site',
						content: appConfig.seo.twitter.site,
					},
					{
						name: 'twitter:creator',
						content: appConfig.seo.twitter.creator,
					},
					{
						name: 'twitter:card',
						content: appConfig.seo.twitter.card,
					},
					{
						name: 'twitter:title',
						content: appConfig.seo.twitter.title,
					},
					{
						name: 'twitter:description',
						content: appConfig.seo.twitter.description,
					},
					{
						name: 'twitter:image',
						content: appConfig.seo.twitter.image,
					},
					// Structured data -> ld+json
					{
						'script:ld+json': data.cart,
					},
					{
						tagname: 'link',
						rel: 'canonical',
						href: `${data.ENV.SERVER_NAME}${location.pathname}`,
					},
					// I18next
					...defaultMetaI18next<typeof ROOT_I18NEXT>({
						I18NEXT: ROOT_I18NEXT,
						locale: data.locale,
					}),
					// Preload current lang flag
					{
						tagname: 'link',
						rel: 'preload',
						href: `/pwa/flags/${localesMap[data.locale]!.img.filename}`,
						as: 'image/svg+xml',
					},
					// Preload images
					/*
  ...(data.cart.items?.filter(({ variant }: GetOrderItem) => (
    (variant as ProductVariantNormalized).images?.[0] ||
    ((variant as ProductVariantNormalized).product as ProductNormalized).images![0]
  )) || []).map(({ variant }: GetOrderItem) => {
    const path =
      (variant as ProductVariantNormalized).images?.[0].path
      || ((variant as ProductVariantNormalized).product as ProductNormalized).images![0].path!

    return {
      tagname: 'link',
      rel: 'preload',
      href: imagePathFilter({ path, filter: IMAGE_FILTERS.gridFilter }),
      as: 'image',
    }
  })
   */
				]
			: [],
		(a, b) => isEqual(a, b),
	)
}

/* -------------------------------------------------------------------------------------------------
 * Links
 * -----------------------------------------------------------------------------------------------*/

const links: LinksFunction = () => {
	return [
		// Preload CSS as a resource to avoid render blocking
		{
			rel: 'preload',
			href: globalStylesheetHref,
			as: 'style',
		},
		{
			rel: 'preload',
			href: keenSliderStylesheetHref,
			as: 'style',
		},
		// These should match the css preloads above to avoid css as render blocking resource
		{
			rel: 'stylesheet',
			href: globalStylesheetHref,
		},
		{
			rel: 'stylesheet',
			href: keenSliderStylesheetHref,
		},
		// Languages as alternate
		{
			rel: 'alternate',
			hrefLang: 'x-default',
			href: `https://suwe-kosmetik.de/${i18nConfig.defaultLng}`,
		},
		...i18nConfig.supportedLngs.map((lng) => ({
			rel: 'alternate',
			hrefLang: lng,
			href: `https://suwe-kosmetik.de/${lng}`,
		})),
	].filter(Boolean)
}

/* -------------------------------------------------------------------------------------------------
 * Should revalidate
 * -----------------------------------------------------------------------------------------------*/

const shouldRevalidate = ({
	currentUrl,
	formData,
	nextUrl,
	defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) => {
	if (
		currentUrl.searchParams.get(
			PRODUCTS_INDEX_SELECT_TAXON_SEARCH_PARAMS.taxon,
		) !==
			nextUrl.searchParams.get(
				PRODUCTS_INDEX_SELECT_TAXON_SEARCH_PARAMS.taxon,
			) ||
		(currentUrl.searchParams.has(
			PRODUCTS_INDEX_SELECT_TAXON_SEARCH_PARAMS.taxon,
		) &&
			!nextUrl.searchParams.has(
				PRODUCTS_INDEX_SELECT_TAXON_SEARCH_PARAMS.taxon,
			)) ||
		formData?.get(ROOT_INPUTS.subaction) === ADD_TO_WISHLIST_FORM.subaction ||
		formData?.get(ROOT_INPUTS.subaction) ===
			REMOVE_FROM_WISHLIST_FORM.subaction ||
		formData?.get(ROOT_INPUTS.subaction) ===
			CREATE_PRODUCT_REVIEW_FORM.subaction ||
		formData?.get(ROOT_INPUTS.subaction) === EDIT_ADDRESS_FORM.subaction ||
		formData?.get(ROOT_INPUTS.subaction) === CREATE_ADDRESS_FORM.subaction ||
		formData?.get(ROOT_INPUTS.subaction) === REMOVE_ADDRESS_FORM.subaction ||
		formData?.get(ROOT_INPUTS.subaction) ===
			RESEND_VERIFICATION_EMAIL_FORM.subaction ||
		formData?.get(ROOT_INPUTS.subaction) ===
			SET_DEFAULT_ADDRESS_FORM.subaction ||
		formData?.get(ROOT_INPUTS.subaction) === NEWSLETTER_FORM.subaction ||
		currentUrl.searchParams.get(ROOT_SEARCH_PARAMS.page) !==
			nextUrl.searchParams.get(ROOT_SEARCH_PARAMS.page) ||
		(currentUrl.searchParams.has(ROOT_SEARCH_PARAMS.page) &&
			!nextUrl.searchParams.has(ROOT_SEARCH_PARAMS.page))
	) {
		return false
	}

	return defaultShouldRevalidate
}

/* -----------------------------------------------------------------------------------------------*/

export { shouldRevalidate, links, meta, handle }
